import { ParentProps, createMemo } from 'solid-js';
import { requireSortableContext } from '../SortableContext';
import Draggable from '../../st-draggable/st-draggable';
import styles from '../st-sortable.module.css';

export class SortableItemProps<T> {
    item!: T;
    index!: number;
    key!: string | number;
    disabled?: boolean = false;
    targets?: string;              // Optional: allow dragging to other zones
    animation?: boolean = true;
    animationDuration?: number = 200;
}

export default function Item<T>(props: ParentProps<SortableItemProps<T>>) {
    const ctx = requireSortableContext<T>();

    let itemRef!: HTMLDivElement;

    const isDragging = createMemo(() => ctx.draggedIndex() === props.index);
    const isDropTarget = createMemo(() => ctx.dropIndex() === props.index);
    const dropPosition = createMemo(() => ctx.dropPosition());

    const className = createMemo(() => {
        const classes = [styles['sortable-item']];
        if (isDragging()) classes.push(styles['dragging']);
        if (isDropTarget()) {
            classes.push(dropPosition() === 'above'
                ? styles['drop-above']
                : styles['drop-below']
            );
        }
        return classes.join(' ');
    });

    // Calculate drop position based on mouse Y position
    const calculateDropPosition = (ev: MouseEvent): 'above' | 'below' => {
        const rect = itemRef.getBoundingClientRect();
        const midpoint = rect.top + (rect.height / 2);
        return ev.clientY < midpoint ? 'above' : 'below';
    };

    // Mouseover handler for drop zone detection
    const handleMouseOver = (ev: MouseEvent) => {
        if (ctx.draggedIndex() !== undefined && ctx.draggedIndex() !== props.index) {
            ctx.setDropIndex(props.index);
            ctx.setDropPosition(calculateDropPosition(ev));
        }
    };

    const handleMouseLeave = () => {
        if (ctx.dropIndex() === props.index) {
            ctx.setDropIndex(undefined);
            ctx.setDropPosition(undefined);
        }
    };

    return (
        <div
            ref={itemRef}
            class={className()}
            onMouseOver={handleMouseOver}
            onMouseLeave={handleMouseLeave}
        >
            <Draggable
                item={props.item}
                disabled={props.disabled}
                targets={props.targets}
                onDragStart={() => ctx.setDraggedIndex(props.index)}
                onDragEnd={() => {
                    const dragIdx = ctx.draggedIndex();
                    const dropIdx = ctx.dropIndex();

                    if (dragIdx !== undefined && dropIdx !== undefined && dragIdx !== dropIdx) {
                        // Calculate final index based on drop position
                        const finalIndex = dropPosition() === 'above' ? dropIdx : dropIdx + 1;
                        ctx.moveItem(dragIdx, finalIndex);
                    }

                    // Cleanup
                    ctx.setDraggedIndex(undefined);
                    ctx.setDropIndex(undefined);
                    ctx.setDropPosition(undefined);
                }}
            >
                {props.children}
            </Draggable>
        </div>
    );
}
